import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Image, { TRANSFORMATIONS } from 'components/uiLibrary/Image';
import Loader from 'components/uiLibrary/Loader';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import InfiniteListPage, { PageLoading } from 'components/Globals/Layout/InfiniteListPage';
import MasonryView from 'components/Globals/Media/MasonryView';
import SectionBlock from 'components/Globals/SectionBlock';

import { TP, ENTITY_MAIN_TABS, SORT_OPTION_VALUES } from 'constants/index';

import withLightbox from 'utils/hocs/withLightbox';
import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';
import { getFillerText, getMediaSource } from 'utils/media';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Media/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import CardWithProductionInfo from '../CardWithProductionInfo';

import classes from './PhotoListing.module.scss';

const FEATURED_SECTION_TITLES = {
  [ENTITY_TYPES.PROFILE]: `FEATURED_PHOTOS`,
  [ENTITY_TYPES.ORGANIZATION]: `FEATURED_PHOTOS`,
  [ENTITY_TYPES.PRODUCTION]: `${TP}.FN_TAB_PHOTOS`,
};

const PhotoGroup = withLightbox(({ photos, showLightbox, showProductionInfo = false, trackingData }) => {
  const openLightbox = mediaId => {
    showLightbox({ data: photos, mediaId, trackingData });
  };

  return (
    <div className={classes.group}>
      {photos?.map(photo => (
        <CardWithProductionInfo
          key={photo?.id}
          production={photo?.production}
          disabled={!showProductionInfo}
          trackingData={trackingData}
          styles={{
            footer: classes.cardFooter,
            root: classes.card,
          }}
        >
          <Image
            src={getMediaSource(photo)}
            blurHash={photo?.blurHash}
            className={classes.image}
            attribution={{
              source_info: photo?.source_info,
              credit: photo?.credit,
              copyright: photo?.copyright,
            }}
            transformations={
              photo?.isPortrait ? TRANSFORMATIONS.GALLERY_PORTRAIT_VIEW : TRANSFORMATIONS.GALLERY_LANDSCAPE_VIEW
            }
            height={182}
            onClick={() => openLightbox(photo?.id)}
            trackingData={trackingData}
            useIntersectionObserver
            disableNextImage
            lazy
            {...(photo?.title && { alt: photo?.title, title: photo?.title })}
          />
        </CardWithProductionInfo>
      ))}
    </div>
  );
});

const AllPhotos = ({ entity }) => {
  const { obRouteContext } = useAppContext();
  const { page, filterParams } = obRouteContext || {};
  const { entityType } = page || {};
  const entityId = entity?.id;
  const shouldFetchFeaturedPhotos = entity?.stats?.media?.images?.featured?.exists;

  return (
    <InfiniteListPage
      query={queries.getFeaturedPhotos({
        entityType,
        entityId,
        filters: filterParams,
        queryConfig: {
          enabled: shouldFetchFeaturedPhotos,
        },
      })}
      disabled={!shouldFetchFeaturedPhotos}
      disableQueryUpdate
    >
      {({ pages, hasNextPage }) => (
        <>
          <MasonryView
            data={pages?.flatMap(item => item.data)}
            mediaContent={entity?.metaInfo}
            fillerText={getFillerText({ entity, entityType })}
          />
          {hasNextPage && <PageLoading defaultLoader />}
          {(!hasNextPage || !shouldFetchFeaturedPhotos) && <AllProductionPhotos entity={entity} />}
        </>
      )}
    </InfiniteListPage>
  );
};

const AllProductionPhotos = () => {
  const { entity } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { page, filterParams } = obRouteContext || {};
  const { entityType } = page || {};
  const entityId = entity?.id;
  const showProductionPhotos = entity?.stats?.media?.images?.production?.exists;

  return (
    <InfiniteListPage
      query={queries.getProductionPhotos({
        entityType,
        entityId,
        filters: {
          ...filterParams,
          sort: SORT_OPTION_VALUES.CUSTOM_DATE,
        },
        queryConfig: {
          enabled: showProductionPhotos,
        },
      })}
      disabled={!showProductionPhotos}
      disableQueryUpdate
    >
      {({ pages }) => (
        <>
          <MasonryView
            data={pages?.flatMap(item => item.data)}
            mediaContent={entity?.metaInfo}
            fillerText={getFillerText({ entity, entityType })}
          />
          <PageLoading defaultLoader />
        </>
      )}
    </InfiniteListPage>
  );
};

const FeaturedPhotos = ({ entity, limit = 10, sectionProps = {}, trackingData }) => {
  const { t } = useTranslation('NS_ENTITY_STUB_PAGE');
  const { navigate } = usePageContext();
  const { obRouteContext } = useAppContext();
  const { page, filterParams } = obRouteContext || {};
  const { entityType } = page || {};
  const entityId = entity?.id;
  const { isTablet } = useDeviceTypeLayouts();
  const { data, isLoading } = useQuery(
    queries.getFeaturedPhotos({ entityId, entityType, filters: filterParams, limit }),
  );

  const { data: productionPhotos } = useQuery(
    queries.getProductionPhotos({
      entityType,
      entityId,
      filters: {
        ...filterParams,
        sort: SORT_OPTION_VALUES.CUSTOM_DATE,
      },
      limit: limit - (entity?.stats?.media?.images?.featured?.total || 0),
      queryConfig: {
        enabled:
          (entity?.stats?.media?.images?.featured?.total || 0) < limit &&
          entity?.stats?.media?.images?.production?.exists,
      },
    }),
  );

  const { linkProps, photos, secondGridStartIndex } = useMemo(() => {
    if (data?.data?.length === 0 && productionPhotos?.data?.length === 0) {
      return { linkProps: {}, photos: [] };
    }

    const items = [...(data?.data || [])];

    if (items?.length < limit && productionPhotos?.data?.length) {
      items.push(...(productionPhotos?.data || []));
    }

    return {
      linkProps: navigate.getLinkProps({ entityType, entity, path: ENTITY_MAIN_TABS.IMAGES, pro: false }),
      photos: items,
      secondGridStartIndex: Math.round(((data?.data?.length || 0) + (productionPhotos?.data?.length || 0)) / 2),
    };
  }, [data?.data, entity, entityType, limit, navigate, productionPhotos?.data]);

  return (
    <SectionBlock
      title={t(FEATURED_SECTION_TITLES[entityType])}
      linkProps={linkProps}
      seeAllTrackingData={{ ...trackingData, component: COMPONENTS.SEE_ALL_CTA }}
      {...sectionProps}
    >
      {isTablet ? (
        <HorizontalScroller>
          <PhotoGroup photos={photos} trackingData={trackingData} />
        </HorizontalScroller>
      ) : (
        <div className={classes.featuredPhotos}>
          <MasonryView
            addFillerMedia={secondGridStartIndex <= 0}
            data={photos.slice(0, secondGridStartIndex)}
            mediaContent={entity?.metaInfo}
            fillerText={getFillerText({ entity, entityType })}
          />
          {photos?.length > secondGridStartIndex && (
            <MasonryView
              data={photos.slice(secondGridStartIndex, photos?.length)}
              mediaContent={entity?.metaInfo}
              fillerText={getFillerText({ entity, entityType })}
            />
          )}
        </div>
      )}
      {isLoading && <Loader />}
    </SectionBlock>
  );
};

const PhotoListing = ({ entity, limit, sectionProps, isOverview = false, trackingData }) => {
  if (isOverview) {
    return <FeaturedPhotos entity={entity} trackingData={trackingData} limit={limit} sectionProps={sectionProps} />;
  }

  return <AllPhotos entity={entity} />;
};

PhotoListing.propTypes = {
  limit: PropTypes.number,
};

export default PhotoListing;
